import type { Bilingual, Crew, ProductCategory, ProductIcon, RoomIcon, Weekday } from './types';

/**
 * Starter routines offered during first-run setup. Both languages are written in advance,
 * so they never cost a translation. Mike's wife reviews this file (decision 18).
 */

/** How often a starter task happens: every work day, twice a week, or once a week. */
export type Frequency = 'daily' | 'twice' | 'weekly';

export interface CatalogTask {
  name: Bilingual;
  minutes: number;
  frequency: Frequency;
  instructions?: Bilingual;
}

/** Rooms of the same kind share their task ideas: every bathroom is offered every bathroom task. */
export type RoomFamily = 'kitchen' | 'bath' | 'bedroom' | 'living' | 'laundry' | 'hallways' | 'patio' | 'yard';

export interface CatalogRoom {
  id: string;
  family: RoomFamily;
  /** The garden's areas belong to the gardener; everything else to the house (default). */
  crew?: Crew;
  name: Bilingual;
  icon: RoomIcon;
  /** Rooms a house often has more than one of; setup offers a count instead of a yes or no. */
  countable?: boolean;
  /** Ticked by default in setup. */
  common?: boolean;
  tasks: CatalogTask[];
}

const SWEEP_MOP: CatalogTask = { name: { en: 'Sweep and mop', es: 'Barrer y trapear' }, minutes: 10, frequency: 'twice' };
const CHANGE_SHEETS: CatalogTask = {
  name: { en: 'Change the sheets', es: 'Cambiar sábanas' },
  minutes: 15,
  frequency: 'weekly',
  instructions: {
    en: 'Clean sheets are in the hallway closet. Put the used ones in the laundry.',
    es: 'Las sábanas limpias están en el clóset del pasillo. Lleva las usadas a la lavandería.',
    esUsted: 'Las sábanas limpias están en el clóset del pasillo. Lleve las usadas a la lavandería.',
  },
};

export const catalogRooms: CatalogRoom[] = [
  {
    id: 'kitchen',
    family: 'kitchen',
    name: { en: 'Kitchen', es: 'Cocina' },
    icon: 'cooking-pot',
    common: true,
    tasks: [
      { name: { en: 'Wipe down the counters', es: 'Limpiar barra' }, minutes: 10, frequency: 'daily' },
      {
        name: { en: 'Clean the stove', es: 'Limpiar estufa' },
        minutes: 15,
        frequency: 'daily',
        instructions: {
          en: 'Use a microfiber cloth. Do not use steel wool, it scratches the surface.',
          es: 'Usa una microfibra. No uses fibra metálica porque raya la superficie.',
          esUsted: 'Use una microfibra. No use fibra metálica porque raya la superficie.',
        },
      },
      { name: { en: 'Scrub the sink', es: 'Lavar fregadero' }, minutes: 5, frequency: 'daily' },
      { name: { en: 'Take out the trash', es: 'Sacar la basura' }, minutes: 5, frequency: 'daily' },
      {
        name: { en: 'Sweep and mop', es: 'Barrer y trapear' },
        minutes: 15,
        frequency: 'daily',
        instructions: {
          en: 'Use the floor cleaner, half a cap per bucket of water.',
          es: 'Usa el limpiador de pisos, media tapa por cubeta de agua.',
          esUsted: 'Use el limpiador de pisos, media tapa por cubeta de agua.',
        },
      },
      { name: { en: 'Clean the outside of the fridge', es: 'Limpiar el refrigerador por fuera' }, minutes: 5, frequency: 'weekly' },
    ],
  },
  {
    id: 'bathroom',
    family: 'bath',
    name: { en: 'Bathroom', es: 'Baño' },
    icon: 'bath',
    countable: true,
    common: true,
    tasks: [
      { name: { en: 'Clean the toilet', es: 'Limpiar inodoro' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Clean the shower', es: 'Limpiar regadera' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Clean the mirror and sink', es: 'Limpiar espejo y lavabo' }, minutes: 5, frequency: 'twice' },
      { name: { en: 'Mop the floor', es: 'Trapear' }, minutes: 5, frequency: 'twice' },
      { name: { en: 'Change the towels', es: 'Cambiar toallas' }, minutes: 5, frequency: 'weekly' },
      { name: { en: 'Refill toilet paper and soap', es: 'Reponer papel y jabón' }, minutes: 5, frequency: 'twice' },
    ],
  },
  {
    id: 'half-bath',
    family: 'bath',
    name: { en: 'Half bath', es: 'Medio baño' },
    icon: 'toilet',
    tasks: [
      { name: { en: 'Clean the toilet and sink', es: 'Limpiar inodoro y lavabo' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Refill toilet paper and soap', es: 'Reponer papel y jabón' }, minutes: 5, frequency: 'twice' },
    ],
  },
  {
    id: 'main-bedroom',
    family: 'bedroom',
    name: { en: 'Main bedroom', es: 'Recámara principal' },
    icon: 'bed-double',
    common: true,
    tasks: [
      { name: { en: 'Make the bed', es: 'Tender la cama' }, minutes: 5, frequency: 'daily' },
      { name: { en: 'Dust the furniture', es: 'Sacudir muebles' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Vacuum or sweep', es: 'Aspirar o barrer' }, minutes: 15, frequency: 'twice' },
      CHANGE_SHEETS,
    ],
  },
  {
    id: 'bedroom',
    family: 'bedroom',
    name: { en: 'Bedroom', es: 'Recámara' },
    icon: 'bed',
    countable: true,
    tasks: [
      { name: { en: 'Make the bed', es: 'Tender la cama' }, minutes: 5, frequency: 'daily' },
      { name: { en: 'Dust', es: 'Sacudir' }, minutes: 10, frequency: 'weekly' },
      SWEEP_MOP,
      CHANGE_SHEETS,
    ],
  },
  {
    id: 'kids-bedroom',
    family: 'bedroom',
    name: { en: "Kids' bedroom", es: 'Recámara de niños' },
    icon: 'bed',
    countable: true,
    tasks: [
      { name: { en: 'Make the beds', es: 'Tender las camas' }, minutes: 5, frequency: 'daily' },
      { name: { en: 'Pick up the toys', es: 'Recoger juguetes' }, minutes: 10, frequency: 'daily' },
      SWEEP_MOP,
      CHANGE_SHEETS,
    ],
  },
  {
    id: 'living-room',
    family: 'living',
    name: { en: 'Living room', es: 'Sala' },
    icon: 'sofa',
    common: true,
    tasks: [
      { name: { en: 'Dust', es: 'Sacudir' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Vacuum the couch', es: 'Aspirar el sillón' }, minutes: 10, frequency: 'weekly' },
      { name: { en: 'Sweep and mop', es: 'Barrer y trapear' }, minutes: 15, frequency: 'twice' },
    ],
  },
  {
    id: 'dining-room',
    family: 'living',
    name: { en: 'Dining room', es: 'Comedor' },
    icon: 'utensils',
    tasks: [
      { name: { en: 'Wipe the table and chairs', es: 'Limpiar mesa y sillas' }, minutes: 10, frequency: 'daily' },
      SWEEP_MOP,
    ],
  },
  {
    id: 'tv-room',
    family: 'living',
    name: { en: 'TV room', es: 'Cuarto de TV' },
    icon: 'tv',
    tasks: [
      { name: { en: 'Dust', es: 'Sacudir' }, minutes: 10, frequency: 'weekly' },
      { name: { en: 'Sweep and mop', es: 'Barrer y trapear' }, minutes: 10, frequency: 'weekly' },
    ],
  },
  {
    id: 'office',
    family: 'living',
    name: { en: 'Office', es: 'Estudio' },
    icon: 'briefcase',
    tasks: [
      { name: { en: 'Dust the desk', es: 'Sacudir el escritorio' }, minutes: 5, frequency: 'weekly' },
      { name: { en: 'Empty the wastebasket', es: 'Vaciar el bote de basura' }, minutes: 5, frequency: 'twice' },
      { name: { en: 'Vacuum or sweep', es: 'Aspirar o barrer' }, minutes: 10, frequency: 'weekly' },
    ],
  },
  {
    id: 'laundry',
    family: 'laundry',
    name: { en: 'Laundry room', es: 'Lavandería' },
    icon: 'washing-machine',
    common: true,
    tasks: [
      {
        name: { en: 'Wash and hang the laundry', es: 'Lavar y tender ropa' },
        minutes: 30,
        frequency: 'twice',
        instructions: {
          en: 'Wash white clothes separately, in cold water.',
          es: 'Lava la ropa blanca aparte, con agua fría.',
          esUsted: 'Lave la ropa blanca aparte, con agua fría.',
        },
      },
      { name: { en: 'Fold and put away clothes', es: 'Doblar y guardar ropa' }, minutes: 20, frequency: 'twice' },
      { name: { en: 'Iron', es: 'Planchar' }, minutes: 30, frequency: 'weekly' },
    ],
  },
  {
    id: 'hallways',
    family: 'hallways',
    name: { en: 'Entry and hallways', es: 'Entrada y pasillos' },
    icon: 'door-open',
    tasks: [
      { name: { en: 'Sweep and mop the hallways', es: 'Barrer y trapear pasillos' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Clean the stairs', es: 'Limpiar escaleras' }, minutes: 10, frequency: 'weekly' },
    ],
  },
  {
    id: 'patio',
    family: 'patio',
    name: { en: 'Patio', es: 'Patio' },
    icon: 'trees',
    tasks: [
      { name: { en: 'Sweep the patio', es: 'Barrer el patio' }, minutes: 15, frequency: 'weekly' },
      { name: { en: 'Wipe the outdoor furniture', es: 'Limpiar muebles de exterior' }, minutes: 10, frequency: 'weekly' },
      { name: { en: 'Water the plants', es: 'Regar plantas' }, minutes: 10, frequency: 'twice' },
    ],
  },
];

/** Less common tasks, offered while an owner types a new task. Both languages written in advance. */
const extraTasks: (CatalogTask & { families: RoomFamily[] })[] = [
  { name: { en: 'Clean the microwave', es: 'Limpiar el microondas' }, minutes: 10, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Clean the inside of the fridge', es: 'Limpiar el refrigerador por dentro' }, minutes: 20, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Wash the dishes', es: 'Lavar los trastes' }, minutes: 20, frequency: 'daily', families: ['kitchen'] },
  { name: { en: 'Load the dishwasher', es: 'Llenar la lavavajillas' }, minutes: 10, frequency: 'daily', families: ['kitchen'] },
  { name: { en: 'Empty the dishwasher', es: 'Vaciar la lavavajillas' }, minutes: 10, frequency: 'daily', families: ['kitchen'] },
  { name: { en: 'Clean the oven', es: 'Limpiar el horno' }, minutes: 30, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Clean the range hood', es: 'Limpiar la campana' }, minutes: 10, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Wipe the cabinet doors', es: 'Limpiar las puertas de la alacena' }, minutes: 10, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Wipe the small appliances', es: 'Limpiar los electrodomésticos' }, minutes: 10, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Clean the bathtub', es: 'Limpiar la tina' }, minutes: 10, frequency: 'twice', families: ['bath'] },
  { name: { en: 'Scrub the grout', es: 'Tallar las juntas' }, minutes: 20, frequency: 'weekly', families: ['bath'] },
  { name: { en: 'Wash the bath mats', es: 'Lavar los tapetes del baño' }, minutes: 10, frequency: 'weekly', families: ['bath'] },
  { name: { en: 'Empty the bathroom trash', es: 'Vaciar el bote de basura del baño' }, minutes: 5, frequency: 'twice', families: ['bath'] },
  { name: { en: 'Organize the closet', es: 'Ordenar el clóset' }, minutes: 20, frequency: 'weekly', families: ['bedroom'] },
  { name: { en: 'Put away clean clothes', es: 'Guardar la ropa limpia' }, minutes: 10, frequency: 'twice', families: ['bedroom', 'laundry'] },
  { name: { en: 'Wash the sheets', es: 'Lavar las sábanas' }, minutes: 30, frequency: 'weekly', families: ['laundry', 'bedroom'] },
  { name: { en: 'Wash the towels', es: 'Lavar las toallas' }, minutes: 30, frequency: 'weekly', families: ['laundry', 'bath'] },
  { name: { en: 'Iron the shirts', es: 'Planchar las camisas' }, minutes: 30, frequency: 'weekly', families: ['laundry'] },
  { name: { en: 'Clean the windows', es: 'Limpiar las ventanas' }, minutes: 20, frequency: 'weekly', families: ['living', 'bedroom', 'kitchen'] },
  { name: { en: 'Clean the mirrors', es: 'Limpiar los espejos' }, minutes: 10, frequency: 'weekly', families: ['bath', 'bedroom', 'hallways'] },
  { name: { en: 'Dust the blinds', es: 'Sacudir las persianas' }, minutes: 15, frequency: 'weekly', families: ['living', 'bedroom'] },
  { name: { en: 'Dust the ceiling fans', es: 'Sacudir los ventiladores de techo' }, minutes: 10, frequency: 'weekly', families: ['living', 'bedroom'] },
  { name: { en: 'Wipe the baseboards', es: 'Limpiar los zoclos' }, minutes: 20, frequency: 'weekly', families: ['living', 'bedroom', 'hallways'] },
  { name: { en: 'Wipe light switches and door handles', es: 'Limpiar apagadores y manijas' }, minutes: 10, frequency: 'weekly', families: ['hallways', 'living', 'bath', 'kitchen'] },
  { name: { en: 'Vacuum the rugs', es: 'Aspirar los tapetes' }, minutes: 10, frequency: 'twice', families: ['living', 'bedroom'] },
  { name: { en: 'Polish the wood furniture', es: 'Pulir los muebles de madera' }, minutes: 15, frequency: 'weekly', families: ['living', 'bedroom'] },
  { name: { en: 'Clean the glass doors', es: 'Limpiar las puertas de vidrio' }, minutes: 10, frequency: 'weekly', families: ['living', 'patio'] },
  { name: { en: 'Water the indoor plants', es: 'Regar las plantas de adentro' }, minutes: 10, frequency: 'twice', families: ['living'] },
  { name: { en: 'Empty the wastebaskets', es: 'Vaciar los botes de basura' }, minutes: 5, frequency: 'twice', families: ['living', 'bedroom'] },
  { name: { en: 'Take out the recycling', es: 'Sacar el reciclaje' }, minutes: 5, frequency: 'weekly', families: ['kitchen'] },
  { name: { en: 'Wash the pet bowls', es: 'Lavar los platos de la mascota' }, minutes: 5, frequency: 'daily', families: ['kitchen'] },
  { name: { en: 'Sweep the front entrance', es: 'Barrer la entrada' }, minutes: 10, frequency: 'weekly', families: ['hallways', 'patio'] },
  { name: { en: 'Shake out the doormats', es: 'Sacudir los tapetes de la entrada' }, minutes: 5, frequency: 'weekly', families: ['hallways'] },
  { name: { en: 'Sweep the garage', es: 'Barrer la cochera' }, minutes: 20, frequency: 'weekly', families: ['patio', 'yard'] },
  { name: { en: 'Check the sprinklers', es: 'Revisar los aspersores' }, minutes: 10, frequency: 'weekly', families: ['yard'] },
  { name: { en: 'Prune the small trees', es: 'Podar los árboles pequeños' }, minutes: 30, frequency: 'weekly', families: ['yard'] },
  { name: { en: 'Check the plants for pests', es: 'Revisar las plantas por plagas' }, minutes: 10, frequency: 'weekly', families: ['yard'] },
  { name: { en: 'Clean the outdoor furniture', es: 'Limpiar los muebles del jardín' }, minutes: 15, frequency: 'weekly', families: ['yard', 'patio'] },
  { name: { en: 'Clean the grill', es: 'Limpiar el asador' }, minutes: 15, frequency: 'weekly', families: ['yard', 'patio'] },
];

const sameTask = (a: CatalogTask, b: CatalogTask) => a.name.en.toLowerCase() === b.name.en.toLowerCase();
function unique(list: CatalogTask[]): CatalogTask[] {
  return list.filter((t, i) => list.findIndex((x) => sameTask(x, t)) === i);
}

/**
 * The tasks offered for a room in setup: its own starter tasks (ticked), then the other tasks
 * of rooms of the same kind (not ticked), so every bathroom gets the same choices.
 */
export function roomTaskOptions(catalogId: string): { task: CatalogTask; on: boolean }[] {
  const room = catalogRooms.find((r) => r.id === catalogId);
  if (!room) return [];
  const siblings = catalogRooms.filter((r) => r.family === room.family && r.id !== room.id).flatMap((r) => r.tasks);
  const more = unique(siblings).filter((t) => !room.tasks.some((own) => sameTask(own, t)));
  return [...room.tasks.map((task) => ({ task, on: true })), ...more.map((task) => ({ task, on: false }))];
}

/** A room's kind, from its icon (rooms keep their starter icon). */
export function familyOfIcon(icon: RoomIcon): RoomFamily | undefined {
  return catalogRooms.find((r) => r.icon === icon)?.family;
}

/** Every task idea, those for the given kind of room first. */
export function taskLibrary(family?: RoomFamily): CatalogTask[] {
  const forFamily = [
    ...catalogRooms.filter((r) => r.family === family).flatMap((r) => r.tasks),
    ...extraTasks.filter((t) => family && t.families.includes(family)),
  ];
  return unique([...forFamily, ...catalogRooms.flatMap((r) => r.tasks), ...extraTasks]).map(({ name, minutes, frequency }) => ({ name, minutes, frequency }));
}

const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');

/** Task ideas whose name, in the owner's language, contains what they typed (accents ignored). */
export function searchTaskLibrary(query: string, lang: 'en' | 'es', family?: RoomFamily, limit = 6): CatalogTask[] {
  const q = fold(query);
  const all = taskLibrary(family);
  if (!q) return all.slice(0, limit);
  return all.filter((t) => fold(t.name[lang]).includes(q) && fold(t.name[lang]) !== q).slice(0, limit);
}

/** The task idea with exactly this name in this language, if the owner picked one and did not change it. */
export function findLibraryTask(name: string, lang: 'en' | 'es'): CatalogTask | undefined {
  const n = fold(name);
  return taskLibrary().find((t) => fold(t.name[lang]) === n);
}

/** The garden's starter areas (Mike, 2026-10-14), offered when an owner adds a gardener. */
const GARDEN_ROOMS: CatalogRoom[] = [
  {
    id: 'front-yard',
    family: 'yard',
    crew: 'garden',
    name: { en: 'Front yard', es: 'Jardín del frente' },
    icon: 'flower-2',
    common: true,
    tasks: [
      { name: { en: 'Mow the lawn', es: 'Cortar el pasto' }, minutes: 40, frequency: 'weekly' },
      { name: { en: 'Edge the lawn', es: 'Orillar el pasto' }, minutes: 20, frequency: 'weekly' },
      { name: { en: 'Water the plants and lawn', es: 'Regar plantas y pasto' }, minutes: 20, frequency: 'twice' },
      { name: { en: 'Pull the weeds', es: 'Quitar la hierba mala' }, minutes: 20, frequency: 'weekly' },
      { name: { en: 'Rake up the leaves', es: 'Barrer y recoger hojas' }, minutes: 15, frequency: 'weekly' },
    ],
  },
  {
    id: 'back-yard',
    family: 'yard',
    crew: 'garden',
    name: { en: 'Backyard', es: 'Jardín de atrás' },
    icon: 'tree-pine',
    common: true,
    tasks: [
      { name: { en: 'Mow the lawn', es: 'Cortar el pasto' }, minutes: 40, frequency: 'weekly' },
      { name: { en: 'Edge the lawn', es: 'Orillar el pasto' }, minutes: 20, frequency: 'weekly' },
      { name: { en: 'Water the plants and lawn', es: 'Regar plantas y pasto' }, minutes: 20, frequency: 'twice' },
      { name: { en: 'Trim the bushes', es: 'Podar los arbustos' }, minutes: 30, frequency: 'weekly' },
      { name: { en: 'Rake up the leaves', es: 'Barrer y recoger hojas' }, minutes: 15, frequency: 'weekly' },
    ],
  },
  {
    id: 'pool',
    family: 'yard',
    crew: 'garden',
    name: { en: 'Pool', es: 'Alberca' },
    icon: 'waves',
    tasks: [
      { name: { en: 'Skim the pool', es: 'Sacar las hojas de la alberca' }, minutes: 15, frequency: 'twice' },
      { name: { en: 'Check the pool chlorine', es: 'Revisar el cloro de la alberca' }, minutes: 5, frequency: 'twice' },
      { name: { en: 'Empty the skimmer basket', es: 'Vaciar la canastilla del filtro' }, minutes: 5, frequency: 'weekly' },
      { name: { en: 'Brush the pool walls', es: 'Cepillar las paredes de la alberca' }, minutes: 20, frequency: 'weekly' },
    ],
  },
  {
    id: 'planters',
    family: 'yard',
    crew: 'garden',
    name: { en: 'Pots and planters', es: 'Macetas' },
    icon: 'sprout',
    tasks: [
      { name: { en: 'Water the pots', es: 'Regar las macetas' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Remove dead leaves', es: 'Quitar las hojas secas' }, minutes: 10, frequency: 'weekly' },
      { name: { en: 'Fertilize the plants', es: 'Abonar las plantas' }, minutes: 10, frequency: 'weekly' },
    ],
  },
  {
    id: 'driveway',
    family: 'yard',
    crew: 'garden',
    name: { en: 'Driveway and sidewalk', es: 'Entrada y banqueta' },
    icon: 'car',
    tasks: [
      { name: { en: 'Sweep the sidewalk', es: 'Barrer la banqueta' }, minutes: 10, frequency: 'twice' },
      { name: { en: 'Hose down the driveway', es: 'Lavar la cochera con agua' }, minutes: 15, frequency: 'weekly' },
      { name: { en: 'Take the trash bins out', es: 'Sacar los botes de basura' }, minutes: 5, frequency: 'weekly' },
    ],
  },
];
catalogRooms.push(...GARDEN_ROOMS);

/** The starter areas for one crew (setup offers the house's; adding a gardener offers the garden's). */
export function roomsForCrew(crew: Crew): CatalogRoom[] {
  return catalogRooms.filter((r) => (r.crew ?? 'house') === crew);
}

export interface CatalogProduct {
  name: Bilingual;
  /** Garden supplies belong to the gardener's list; everything else to the house (default). */
  crew?: Crew;
  category: ProductCategory;
  icon: ProductIcon;
}

export const catalogProducts: CatalogProduct[] = [
  { name: { en: 'Bleach', es: 'Cloro' }, category: 'general', icon: 'milk' },
  { name: { en: 'All-purpose cleaner', es: 'Limpiador multiusos' }, category: 'general', icon: 'spray-can' },
  { name: { en: 'Glass cleaner', es: 'Limpiavidrios' }, category: 'general', icon: 'sparkles' },
  { name: { en: 'Microfiber cloths', es: 'Microfibras' }, category: 'general', icon: 'square-stack' },
  { name: { en: 'Dish soap', es: 'Jabón para trastes' }, category: 'kitchen', icon: 'droplets' },
  { name: { en: 'Sponges', es: 'Esponjas' }, category: 'kitchen', icon: 'layers' },
  { name: { en: 'Degreaser', es: 'Desengrasante' }, category: 'kitchen', icon: 'spray-can' },
  { name: { en: 'Bathroom cleaner', es: 'Limpiador de baño' }, category: 'bathroom', icon: 'spray-can' },
  { name: { en: 'Disinfectant', es: 'Desinfectante' }, category: 'bathroom', icon: 'flask-conical' },
  { name: { en: 'Toilet paper', es: 'Papel de baño' }, category: 'bathroom', icon: 'package' },
  { name: { en: 'Floor cleaner', es: 'Limpiador de pisos' }, category: 'floors', icon: 'spray-can' },
  { name: { en: 'Trash bags', es: 'Bolsas de basura' }, category: 'disposables', icon: 'trash-2' },
  { name: { en: 'Paper towels', es: 'Toallas de papel' }, category: 'disposables', icon: 'package' },
  { name: { en: 'Laundry detergent', es: 'Detergente para ropa' }, category: 'other', icon: 'shirt' },
  { name: { en: 'Fabric softener', es: 'Suavizante' }, category: 'other', icon: 'shirt' },
  { name: { en: 'Gloves', es: 'Guantes' }, category: 'other', icon: 'hand' },
  // The garden's starter supplies (Mike, 2026-10-14).
  { name: { en: 'Fertilizer', es: 'Fertilizante' }, category: 'garden', icon: 'leaf', crew: 'garden' },
  { name: { en: 'Gas for the mower', es: 'Gasolina para la podadora' }, category: 'garden', icon: 'fuel', crew: 'garden' },
  { name: { en: 'Trimmer line', es: 'Hilo para la orilladora' }, category: 'garden', icon: 'package', crew: 'garden' },
  { name: { en: 'Leaf bags', es: 'Bolsas para hojas' }, category: 'garden', icon: 'trash-2', crew: 'garden' },
  { name: { en: 'Potting soil', es: 'Tierra para macetas' }, category: 'garden', icon: 'sprout', crew: 'garden' },
  { name: { en: 'Pool chlorine', es: 'Cloro para alberca' }, category: 'garden', icon: 'flask-conical', crew: 'garden' },
  { name: { en: 'Garden gloves', es: 'Guantes de jardín' }, category: 'garden', icon: 'hand', crew: 'garden' },
];

/** Turns a starter frequency into days of the week, given the household's work days. */
export function daysFor(frequency: Frequency, workDays: Weekday[]): Weekday[] {
  const days = [...workDays].sort((a, b) => a - b);
  if (days.length === 0) return [];
  if (frequency === 'daily') return days;
  if (frequency === 'weekly') return [days[days.length - 1]];
  if (days.length === 1) return days;
  // Twice a week: the first work day and one roughly half a week later.
  const second = days.find((d) => d - days[0] >= 3) ?? days[Math.floor(days.length / 2)];
  return [days[0], second];
}
